import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeaturedSection = ({ onResourceClick }) => {
  const featuredResources = [
    {
      id: 'featured-1',
      title: 'AI Survival Imperative Executive Brief',
      description: 'A comprehensive executive briefing on why AI adoption is no longer optional but essential for organizational survival in the next decade.',
      type: 'whitepaper',
      targetRole: 'C-Suite',
      readTime: '15 min read',
      downloads: '2.3K',
      publishDate: 'Dec 2024',
      featured: true,
      pillar: 'AI Survival Strategy',
      gradient: 'from-red-500 to-orange-500'
    },
    {
      id: 'featured-2',
      title: 'Open-Source AI Implementation Guide',
      description: 'Technical deep-dive into building enterprise-grade AI capabilities using open-source tools and frameworks.',
      type: 'guide',
      targetRole: 'Technical',
      readTime: '45 min read',
      downloads: '1.8K',
      publishDate: 'Dec 2024',
      featured: true,
      pillar: 'Open-Source Superiority',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'featured-3',
      title: 'Vendor Lock-in Cost Calculator',
      description: 'Interactive tool to calculate the true cost of vendor dependency and potential savings from open-source migration.',
      type: 'calculator',
      targetRole: 'Procurement',
      readTime: '10 min tool',
      downloads: '3.1K',
      publishDate: 'Jan 2025',
      featured: true,
      pillar: 'Vendor Lock-in Risks',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'whitepaper': return 'FileText';
      case 'calculator': return 'Calculator';
      case 'guide': return 'BookOpen';
      default: return 'File';
    }
  };

  return (
    <section className="mb-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-text-primary mb-4">
          Featured Resources
        </h2>
        <p className="text-lg text-text-secondary max-w-3xl mx-auto">
          Our most impactful resources for accelerating your AI independence journey
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {featuredResources?.map((resource) => (
          <div
            key={resource?.id}
            className="relative bg-white rounded-xl shadow-executive border border-gray-200 hover:shadow-executive-lg transition-all duration-300 group overflow-hidden"
          >
            {/* Gradient Header */}
            <div className={`h-2 bg-gradient-to-r ${resource?.gradient}`}></div>
            
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-colors duration-200">
                  <Icon name={getTypeIcon(resource?.type)} size={24} className="text-text-primary" />
                </div>
                <span className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-3 py-1 rounded-full text-xs font-medium">
                  Featured
                </span>
              </div>

              {/* Content */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-text-primary mb-3 group-hover:text-brand-primary transition-colors duration-200">
                  {resource?.title}
                </h3>
                <p className="text-text-secondary text-sm mb-4 line-clamp-3">
                  {resource?.description}
                </p>
                
                {/* Pillar Tag */}
                <span className="inline-block bg-brand-surface text-brand-primary px-3 py-1 rounded-full text-xs font-medium mb-4">
                  {resource?.pillar}
                </span>
              </div>

              {/* Meta Info */}
              <div className="flex items-center justify-between text-sm text-text-secondary mb-6">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={16} />
                    <span>{resource?.readTime}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Download" size={16} />
                    <span>{resource?.downloads}</span>
                  </div>
                </div>
                <span className="text-xs">{resource?.publishDate}</span>
              </div>

              {/* Action */}
              <Button
                variant="default"
                fullWidth
                className="bg-brand-primary hover:bg-brand-primary/90"
                iconName={resource?.type === 'calculator' ? 'ExternalLink' : 'Download'}
                iconPosition="right"
                onClick={() => onResourceClick(resource)}
              >
                {resource?.type === 'calculator' ? 'Launch Calculator' : 'Download Now'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FeaturedSection;